import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const SCROLL_TOLERANCE = 2;

interface UseSliderOptions {
  itemCount: number;
  infiniteLoop?: boolean;
}

export default function useSlider({ itemCount, infiniteLoop = false }: UseSliderOptions) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState({
    canScroll: true,
    isAtStart: true,
    isAtEnd: false,
    thumbWidthPct: 0,
    thumbPositionPct: 0,
  });

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el && infiniteLoop) el.scrollLeft = el.clientWidth;
  }, [itemCount, infiniteLoop]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (typeof ResizeObserver === 'undefined') return;

    let scrollEndTimer: ReturnType<typeof setTimeout>;
    const measure = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;

      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const canScroll = maxScroll > SCROLL_TOLERANCE;
      const isAtStart = scrollLeft <= SCROLL_TOLERANCE;
      const isAtEnd = scrollLeft >= maxScroll - SCROLL_TOLERANCE;

      const thumbWidthPct = scrollWidth > 0 ? Math.min(100, (clientWidth / scrollWidth) * 100) : 100;
      const thumbPositionPct = maxScroll > 0 ? (scrollLeft / maxScroll) * (100 - thumbWidthPct) : 0;

      setMetrics({
        canScroll,
        isAtStart,
        isAtEnd,
        thumbWidthPct,
        thumbPositionPct,
      });

      if (infiniteLoop) {
        clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(() => {
          if (el.scrollLeft <= SCROLL_TOLERANCE) el.scrollLeft = maxScroll - clientWidth;
          if (el.scrollLeft >= maxScroll - SCROLL_TOLERANCE) el.scrollLeft = clientWidth;
        }, 100);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    el.addEventListener('scroll', measure);
    return () => {
      clearTimeout(scrollEndTimer);
      observer.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [scrollerRef, itemCount, infiniteLoop]);

  const next = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
  }, []);

  const prev = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });
  }, []);

  return {
    scrollerRef,
    next,
    prev,
    ...metrics,
  };
}
